import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Bell, CheckCircle, Mail, AlertTriangle, Workflow, 
  DollarSign, Users, RefreshCw, Check
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? {...n, read: true} : n
      ));
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      for (const n of notifications.filter(n => !n.read)) {
        await axios.put(`${API}/notifications/${n.notification_id}/read`, {}, { withCredentials: true });
      }
      setNotifications(notifications.map(n => ({...n, read: true})));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'hot_lead': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'workflow_failed': return <Workflow className="w-5 h-5 text-red-500" />;
      case 'deal_followup': return <DollarSign className="w-5 h-5 text-amber-500" />;
      case 'contract_renewal': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'new_customer': return <Users className="w-5 h-5 text-green-500" />;
      case 'nurture_email': return <Mail className="w-5 h-5 text-blue-500" />;
      case 'campaign_insights': return <CheckCircle className="w-5 h-5 text-purple-500" />;
      default: return <Bell className="w-5 h-5 text-primary" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">Notifications</h1>
            <p className="text-muted-foreground mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={fetchNotifications}
              className="rounded-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            {unreadCount > 0 && (
              <Button 
                onClick={markAllAsRead}
                className="rounded-full"
              >
                <Check className="w-4 h-4 mr-2" /> Mark All Read
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Bell className="w-5 h-5" /> Recent Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-20 bg-muted/50 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : notifications.length > 0 ? (
              <ScrollArea className="h-[600px]">
                <div className="space-y-3 pr-4">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.notification_id}
                      className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                        notification.read 
                          ? 'bg-muted/20 border-border/50' 
                          : 'bg-primary/5 border-primary/20 hover:bg-primary/10'
                      }`}
                      onClick={() => !notification.read && markAsRead(notification.notification_id)}
                      data-testid={`notification-${notification.notification_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          notification.read ? 'bg-muted' : 'bg-primary/10'
                        }`}>
                          {getIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className={`font-medium ${notification.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {notification.email_sent && (
                                <Badge variant="outline" className="text-xs">
                                  <Mail className="w-3 h-3 mr-1" /> Emailed
                                </Badge>
                              )}
                              {!notification.read && (
                                <span className="w-2 h-2 rounded-full bg-primary" />
                              )}
                            </div>
                          </div>
                          <p className={`text-sm whitespace-pre-line line-clamp-3 ${
                            notification.read ? 'text-muted-foreground/70' : 'text-muted-foreground'
                          }`}>
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No notifications yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Notifications from your workflows will appear here
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
